/**
 * API 客户端（M0 建立，M1 扩展）。
 * - 后台统一前缀 /api/admin/v1；
 * - 自动附加 Access Token；
 * - 统一解析 { data, meta, requestId } 与 { error: { code, message } } 结构；
 * - M1 新增：PUT 方法、原始字节上传（两阶段上传阶段二）、XLSX 二进制下载。
 */
import type { ApiFailure, ApiMeta, ApiSuccess, ErrorCode } from '@liyu/shared-types';

const ADMIN_API_PREFIX = '/api/admin/v1';

export interface ApiErrorPayload {
  code: ErrorCode;
  message: string;
  details?: Record<string, unknown>;
  requestId?: string;
  status: number;
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details?: Record<string, unknown>;
  readonly requestId?: string;

  constructor(payload: ApiErrorPayload) {
    super(payload.message);
    this.name = 'ApiError';
    this.code = payload.code;
    this.status = payload.status;
    if (payload.details !== undefined) {
      this.details = payload.details;
    }
    if (payload.requestId !== undefined) {
      this.requestId = payload.requestId;
    }
  }
}

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  /** 原始字节流（文件上传阶段二使用，不做 JSON 序列化） */
  rawBody?: BodyInit;
  /** 原始字节流的 Content-Type */
  contentType?: string;
  query?: Record<string, string | number | boolean | undefined>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${ADMIN_API_PREFIX}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

function buildHeaders(options: RequestOptions): Record<string, string> {
  const headers: Record<string, string> = {};
  if (options.rawBody === undefined) {
    headers['content-type'] = 'application/json';
  } else if (options.contentType) {
    headers['content-type'] = options.contentType;
  }
  if (accessToken) {
    headers.authorization = `Bearer ${accessToken}`;
  }
  return headers;
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta?: ApiMeta; requestId: string }> {
  const response = await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers: buildHeaders(options),
    // 后台 Refresh Token 通过 HttpOnly Cookie 传递，必须携带凭据
    credentials: 'include',
    ...(options.rawBody !== undefined
      ? { body: options.rawBody }
      : options.body !== undefined
        ? { body: JSON.stringify(options.body) }
        : {}),
  });

  let payload: ApiSuccess<T> | ApiFailure;
  try {
    payload = (await response.json()) as ApiSuccess<T> | ApiFailure;
  } catch {
    throw new ApiError({
      code: 'INTERNAL_ERROR',
      message: `服务端返回了非 JSON 响应（HTTP ${response.status}）`,
      status: response.status,
    });
  }

  if (!response.ok || 'error' in payload) {
    const failure = payload as ApiFailure;
    throw new ApiError({
      code: failure.error?.code ?? 'INTERNAL_ERROR',
      message: failure.error?.message ?? '请求失败',
      ...(failure.error?.details ? { details: failure.error.details } : {}),
      ...(failure.requestId ? { requestId: failure.requestId } : {}),
      status: response.status,
    });
  }

  const success = payload as ApiSuccess<T>;
  return {
    data: success.data,
    ...(success.meta ? { meta: success.meta } : {}),
    requestId: success.requestId,
  };
}

/** 下载二进制响应（导入模板、错误报告、活动报名名单 XLSX），携带鉴权头 */
export async function apiDownload(
  path: string,
  fallbackFileName: string,
  query?: RequestOptions['query'],
): Promise<void> {
  const response = await fetch(buildUrl(path, query), {
    method: 'GET',
    headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
    credentials: 'include',
  });

  if (!response.ok) {
    let message = `下载失败（HTTP ${response.status}）`;
    try {
      const failure = (await response.json()) as ApiFailure;
      message = failure.error?.message ?? message;
    } catch {
      // 二进制错误响应无法解析，保留状态码信息
    }
    throw new ApiError({ code: 'INTERNAL_ERROR', message, status: response.status });
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const matched = /filename="?([^";]+)"?/.exec(disposition);
  const fileName = matched?.[1] ?? fallbackFileName;
  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(objectUrl);
}
